import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from './TestAuthContext';
import api from '../utils/api';

// Try to import useAuth, but make it optional for test mode
let useAuthHook: (() => { getToken: () => Promise<string | null> }) | null = null;
try {
  const clerk = require('@clerk/clerk-react');
  useAuthHook = clerk.useAuth;
} catch (e) {
  // Clerk not available
}

interface ColorScheme {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  success: string;
  warning: string;
  error: string;
}

interface BrandingConfig {
  company_name: string;
  company_tagline?: string;
  logo_url?: string;
  logo_dark_url?: string;
  favicon_url?: string;
  colors: ColorScheme;
  contact_email?: string;
  contact_phone?: string;
  website_url?: string;
}

interface BrandingContextType {
  branding: BrandingConfig | null;
  loading: boolean;
  refreshBranding: () => Promise<void>;
}

const DEFAULT_BRANDING: BrandingConfig = {
  company_name: 'ClaimSync',
  company_tagline: 'Business Interruption Calculator',
  colors: {
    primary: '#2563eb',
    secondary: '#06b6d4',
    accent: '#3b82f6',
    background: '#f8fafc',
    text: '#1e293b',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444'
  }
};

const BrandingContext = createContext<BrandingContextType>({
  branding: DEFAULT_BRANDING,
  loading: false,
  refreshBranding: async () => {}
});

export const useBranding = () => useContext(BrandingContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded } = useUser();
  // Safely try to use Clerk auth - may not be available in test mode or outside ClerkProvider
  let getToken: (() => Promise<string | null>) | null = null;
  try {
    if (useAuthHook) {
      const auth = useAuthHook();
      getToken = auth.getToken;
    }
  } catch (e) {
    // useAuth called outside ClerkProvider - that's okay, we'll skip auth
  }
  const [branding, setBranding] = useState<BrandingConfig | null>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(false);

  // Apply CSS variables to document root
  const applyCSSVariables = useCallback((colors: ColorScheme) => {
    const root = document.documentElement;
    
    // Set CSS custom properties
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-secondary', colors.secondary);
    root.style.setProperty('--color-accent', colors.accent);
    root.style.setProperty('--color-background', colors.background);
    root.style.setProperty('--color-text', colors.text);
    root.style.setProperty('--color-success', colors.success);
    root.style.setProperty('--color-warning', colors.warning);
    root.style.setProperty('--color-error', colors.error);
    
    // Light variants (with opacity)
    root.style.setProperty('--color-primary-light', `${colors.primary}20`);
    root.style.setProperty('--color-secondary-light', `${colors.secondary}20`);
    root.style.setProperty('--color-accent-light', `${colors.accent}20`);
    
    // Also update meta theme-color for mobile browsers
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute('content', colors.primary);
    }
    
    console.log('✅ Branding CSS variables applied:', colors.primary);
  }, []);

  const fetchBranding = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      // Use centralized API utility
      const data = await api.get(`/api/v1/branding/${user.id}`, { showErrorToast: false });
      if (data.config) {
        setBranding(data.config);
        applyCSSVariables(data.config.colors);
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
      // Apply default branding on error
      applyCSSVariables(DEFAULT_BRANDING.colors);
    } finally {
      setLoading(false);
    }
  }, [user?.id, applyCSSVariables]);

  const refreshBranding = useCallback(async () => {
    await fetchBranding();
  }, [fetchBranding]);

  // Fetch branding when user is loaded
  useEffect(() => {
    if (isLoaded && user?.id) {
      fetchBranding();
    } else if (isLoaded && !user) {
      // Apply default branding for non-authenticated users
      applyCSSVariables(DEFAULT_BRANDING.colors);
    }
  }, [isLoaded, user?.id, fetchBranding, applyCSSVariables]);

  return (
    <BrandingContext.Provider value={{ branding, loading, refreshBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export default BrandingContext;
